import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedHistoryEntry } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { howMadeMessages as messages } from '../messages/howMadeMessages';
import { directedResultFacts } from '../steps/make/resultRules';
import { confirmedAt } from './howMadeRules';
import { HowMadeSection } from './HowMadeSection';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for "How it was made"; each part uses its own slice of it */
import styles from './HowMade.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Steps 5 and 6 of "How it was made" (design l1): the price, the credits back, and the make. */
const HowMadePriceComponent: React.FC<{ entries: readonly DirectedHistoryEntry[] }> = ({ entries }) => {
  const intl = useIntl();
  const { video } = useDirectedVideoContext();
  const facts = directedResultFacts(video);
  const price = facts.chargedCredits + facts.backCredits;
  const started = confirmedAt(entries);
  const credits = (count: number) => intl.formatMessage(messages.credits, { count });

  return (
    <HowMadeSection
      step="price"
      eyebrow={intl.formatMessage(messages.priceEyebrow)}
      title={intl.formatMessage(messages.priceTitle, { price, charged: credits(facts.chargedCredits) })}
    >
      <div className={styles.cards}>
        <div className={styles.card}>
          <span className={styles.eyebrow}>{intl.formatMessage(messages.priceCard)}</span>
          <dl className={styles.lines}>
            <dt>
              {intl.formatMessage(messages.priceLineSeconds, { count: video.clips.total, seconds: facts.footageSec })}
            </dt>
            <dd>{credits(price)}</dd>
            {facts.backCredits > 0 && (
              <>
                <dt>{intl.formatMessage(messages.backLine, { count: facts.clipsFailed })}</dt>
                <dd className={styles.back}>{credits(facts.backCredits)}</dd>
              </>
            )}
            <dt className={styles.total}>{intl.formatMessage(messages.charged)}</dt>
            <dd className={styles.total}>{credits(facts.chargedCredits)}</dd>
          </dl>
        </div>
        <div className={styles.card}>
          <span className={styles.eyebrow}>{intl.formatMessage(messages.makeCard)}</span>
          <dl className={styles.lines}>
            {started !== null && (
              <>
                <dt>{intl.formatMessage(messages.started)}</dt>
                <dd>
                  {intl.formatDate(started, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </dd>
              </>
            )}
            <dt>{intl.formatMessage(messages.clipsMade)}</dt>
            <dd>{intl.formatMessage(messages.clipsMadeValue, { made: facts.clipsMade, total: video.clips.total })}</dd>
            <dt>{intl.formatMessage(messages.state)}</dt>
            <dd className={clsx(video.status === 'ready' && styles.back)}>
              {intl.formatMessage(video.status === 'ready' ? messages.stateReady : messages.stateMaking)}
            </dd>
          </dl>
        </div>
      </div>
    </HowMadeSection>
  );
};

export const HowMadePrice = TypedMemo(HowMadePriceComponent);
